import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import planService, { Plan } from '@/services/plan.service';
import { useAuth } from '@/contexts/auth.context';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  ArrowLeft,
  Sparkles,
  Users,
  User,
  Crown,
  Zap,
  Shield,
  ChevronRight,
  Info,
  LogOut,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const FeatureItem = ({
  text,
  enabled = true,
  highlight = false,
}: {
  text: string;
  enabled?: boolean;
  highlight?: boolean;
}) => (
  <li
    className={cn(
      'flex items-center gap-3 py-1.5',
      !enabled && 'text-muted-foreground/50 line-through'
    )}
  >
    {enabled ? (
      <CheckCircle2
        className={cn(
          'h-4 w-4 flex-shrink-0',
          highlight ? 'text-amber-500' : 'text-emerald-500'
        )}
      />
    ) : (
      <XCircle className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
    )}
    <span className={cn(
      'text-sm',
      highlight && enabled ? 'font-medium text-amber-600' : enabled ? 'text-muted-foreground' : ''
    )}>
      {text}
    </span>
  </li>
);

export default function PlanSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const initialData = (location.state as { name?: string; email?: string }) || {};
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [accountType, setAccountType] = useState<'individual' | 'family'>('individual');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await planService.listPublicPlans();
        setPlans(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sortedPlans = useMemo(
    () => plans
      .filter(p => (p.account_type || 'individual') === accountType)
      .sort((a, b) => a.price_monthly - b.price_monthly),
    [plans, accountType]
  );

  const onChoose = (plan: Plan) => {
    // Pré-cadastro: usuário ainda não autenticado → coleta dados em /register
    // já passando o plano escolhido para que o backend crie Subscription junto
    // (corrige Bug 1, caso June: Subscriber órfão sem Subscription).
    if (!user) {
      navigate('/register', {
        state: {
          ...initialData,
          plan,
          billingCycle,
          accountType,
        },
      });
      return;
    }

    navigate('/register/checkout', {
      state: {
        ...initialData,
        name: userName,
        email: user?.email || initialData.email,
        plan,
        billingCycle,
        accountType,
      },
    });
  };

  // Get the user name from auth context or from initialData (registration flow)
  const userName = useMemo(() => {
    if (user?.name) return user.name;
    if (initialData.name) return initialData.name;
    return '';
  }, [user?.name, initialData.name]);

  const getDisplayName = (fullName: string) => {
    if (!fullName) return null;
    const parts = fullName.trim().split(' ');
    // Mostrar primeiro nome + último sobrenome se houver mais de 2 partes
    if (parts.length > 2) {
      return `${parts[0]} ${parts[parts.length - 1]}`;
    }
    // Mostrar nome completo se tiver até 2 partes
    return fullName;
  };

  const getPlanIcon = (planType: string) => {
    const iconMap: Record<string, LucideIcon> = {
      free: Shield,
      basic: Shield,
      pro: Zap,
      enterprise: Crown,
    };
    const Icon = iconMap[planType] || Shield;
    return Icon;
  };

  const isPopular = (plan: Plan) => {
    if (plan.is_popular !== undefined) {
      return Boolean(plan.is_popular);
    }
    return plan.type === 'pro';
  };

  const getYearlyDiscount = (plan: Plan) => {
    const monthlyTotal = plan.price_monthly * 12;
    const yearlyTotal = plan.price_yearly;
    if (yearlyTotal >= monthlyTotal) return 0;
    return Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-muted border-t-primary mx-auto" />
          </div>
          <p className="text-muted-foreground text-sm">Carregando planos...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        {/* Header */}
        <header className="sticky top-0 z-10" style={{ background: BRAND_HERO_GRADIENT }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate('/register')}
                className="text-gray-300 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div className="flex items-center gap-2">
                <img
                  src="/logo-librattum.png"
                  alt="Librattum"
                  className="h-8"
                />
              </div>
              {getDisplayName(userName) && (
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-300">
                    Olá, <span className="font-medium text-white">{getDisplayName(userName)}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => logout()}
                    className="text-gray-300 hover:text-white hover:bg-white/10"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Escolha o plano ideal
            </h1>
            <p className="text-lg text-muted-foreground">
              Organize suas finanças pessoais com inteligência e praticidade. 
              Comece grátis e evolua quando precisar.
            </p>
          </div>

          {/* Account Type Selector */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1 bg-muted rounded-xl">
              <button
                onClick={() => setAccountType('individual')}
                className={cn(
                  'flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all',
                  accountType === 'individual'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <User className="h-4 w-4" />
                Individual
              </button>
              <button
                onClick={() => setAccountType('family')}
                className={cn(
                  'flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all',
                  accountType === 'family'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Users className="h-4 w-4" />
                Família
                <Badge variant="secondary" className="ml-1 bg-emerald-100 text-emerald-700 text-xs">
                  Até 5 pessoas
                </Badge>
              </button>
            </div>
          </div>

          {/* Billing Cycle Selector */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center p-1 bg-muted rounded-full">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={cn(
                  'px-6 py-2.5 rounded-full text-sm font-medium transition-all',
                  billingCycle === 'monthly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Mensal
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={cn(
                  'px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2',
                  billingCycle === 'yearly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Anual
                <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                  Economize até 20%
                </span>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {sortedPlans.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground">
                  {accountType === 'family' 
                    ? 'Nenhum plano familiar disponível no momento.' 
                    : 'Nenhum plano individual disponível no momento.'}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1">Entre em contato conosco para mais informações.</p>
              </div>
            ) : (
              sortedPlans.map((plan) => {
                const popular = isPopular(plan);
                const Icon = getPlanIcon(plan.type);
                const discount = getYearlyDiscount(plan);
                const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly / 12;
                const isFree = plan.price_monthly === 0;

                return (
                  <div
                    key={plan.id}
                    className={cn(
                      'relative bg-card rounded-2xl border-2 transition-all duration-300 hover:shadow-xl flex flex-col',
                      popular
                        ? 'border-primary shadow-lg scale-[1.02] lg:-mt-4 lg:mb-4'
                        : 'border-border hover:border-border/80'
                    )}
                  >
                    {/* Popular Badge */}
                    {popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-primary text-white px-4 py-1 text-xs font-semibold shadow-lg">
                          <Sparkles className="h-3 w-3 mr-1" />
                          MAIS POPULAR
                        </Badge>
                      </div>
                    )}

                    {/* Header */}
                    <div className={cn(
                      'p-6 pb-4 text-center',
                      popular && 'pt-8'
                    )}>
                      <div className={cn(
                        'w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center',
                        popular ? 'bg-primary/10' : 'bg-muted'
                      )}>
                        <Icon className={cn(
                          'h-6 w-6',
                          popular ? 'text-primary' : 'text-muted-foreground'
                        )} />
                      </div>
                      
                      <h3 className="text-xl font-bold text-foreground mb-1">
                        {plan.name}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground min-h-[40px]">
                        {plan.description || `Plano ${plan.name} com funcionalidades completas`}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="px-6 py-4 text-center border-t border-border/50">
                      {isFree ? (
                        <div className="text-4xl font-bold text-foreground">
                          Grátis
                        </div>
                      ) : (
                        <>
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-sm text-muted-foreground">R$</span>
                            <span className="text-4xl font-bold text-foreground">
                              {price.toFixed(2).replace('.', ',')}
                            </span>
                            <span className="text-muted-foreground">/mês</span>
                          </div>
                          
                          {billingCycle === 'yearly' && discount > 0 && (
                            <div className="mt-2 flex items-center justify-center gap-2">
                              <span className="text-sm text-muted-foreground line-through">
                                R$ {(plan.price_monthly).toFixed(2).replace('.', ',')}
                              </span>
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                                -{discount}%
                              </Badge>
                            </div>
                          )}
                          
                          {billingCycle === 'yearly' && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Cobrado anualmente: R$ {plan.price_yearly.toFixed(2).replace('.', ',')}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Features */}
                    <div className="px-6 py-4 flex-1 border-t border-border/50">
                      {plan.plan_features && plan.plan_features.length > 0 ? (
                        <ul className="space-y-1">
                          {plan.plan_features.slice(0, 8).map((pf) => (
                            <FeatureItem
                              key={pf.id}
                              text={pf.name}
                              enabled={!!pf.is_enabled}
                              highlight={!!pf.is_highlighted && !!pf.is_enabled}
                            />
                          ))}
                          {plan.plan_features.length > 8 && (
                            <li className="pt-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="text-xs text-primary hover:underline flex items-center gap-1">
                                    <Info className="h-3 w-3" />
                                    +{plan.plan_features.length - 8} recursos
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                  <ul className="text-xs space-y-1">
                                    {plan.plan_features.slice(8).map((pf) => (
                                      <li key={pf.id} className={!pf.is_enabled ? 'text-muted-foreground/50' : ''}>
                                        {pf.is_enabled ? '✓' : '✗'} {pf.name}
                                      </li>
                                    ))}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </li>
                          )}
                        </ul>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground/50">
                          <p className="text-sm">Funcionalidades em preparação</p>
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="p-6 pt-4">
                      <Button
                        className={cn(
                          'w-full py-6 text-base font-semibold transition-all',
                          popular
                            ? 'bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl'
                            : 'bg-foreground text-background hover:bg-foreground/90'
                        )}
                        onClick={() => onChoose(plan)}
                      >
                        {isFree ? 'Começar grátis' : popular ? 'Começar agora' : 'Escolher plano'}
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Family Info */}
          {accountType === 'family' && (
            <div className="mt-12 max-w-2xl mx-auto">
              <div className="bg-gradient-to-r from-primary/5 to-emerald-500/5 rounded-2xl p-6 border border-primary/10">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Plano Família</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Compartilhe sua conta com até 5 pessoas da sua família. 
                      Cada membro terá seu próprio perfil financeiro privado, 
                      mas poderão visualizar finanças compartilhadas da família.
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        Perfis individuais para cada membro
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        Dashboard consolidado da família
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        Despesas futuras em conjunto
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-8 px-8 py-4 bg-muted rounded-2xl">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield className="h-5 w-5" />
                <span className="text-sm font-medium">Pagamento Seguro</span>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">Cancele quando quiser</span>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium">7 dias de garantia</span>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              Todos os planos incluem garantia de satisfação. 
              Cancele com reembolso em até 7 dias após a ativação.
            </p>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
